import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon, InfinityIcon, TruckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { getLegalDoc, legalDocs, LegalSection } from '../data/legal';
import { media } from '../data/product';
import { SiteFooter } from '../components/SiteFooter';
import { PaymentLogos } from '../components/PaymentLogos';
const promises = [{
  icon: InfinityIcon,
  label: 'Lifetime free replacement'
}, {
  icon: RotateCcwIcon,
  label: '30-day returns, we pay shipping'
}, {
  icon: TruckIcon,
  label: 'Ships from the U.S. in 1 business day'
}, {
  icon: ShieldCheckIcon,
  label: 'Real person on support'
}];
export function Legal() {
  const {
    slug = ''
  } = useParams();
  const doc = getLegalDoc(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const renderSection = (section: LegalSection) => <section key={section.heading} className="border-t border-white/10 pt-7" data-id="element-1104" data-mp="3s6067dm8smsf">
      <h2 className={section.fine ? 'font-display text-lg font-bold uppercase tracking-wide text-steel-400' : 'font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl'} data-id="element-1105" data-mp="27kd5iqhsib4r">
        {section.heading}
      </h2>
      <div className={`mt-3 grid max-w-3xl ${section.fine ? 'gap-2' : 'gap-3'}`} data-id="element-1106" data-mp="2cjy4v20vgli0">
        {section.body.map(paragraph => <p key={paragraph} className={section.fine ? 'text-[12px] leading-relaxed text-steel-500' : 'text-[15px] leading-relaxed text-steel-300 sm:text-base'} data-id="element-1107" data-mp="1ov6jathsf1e7">
            {paragraph}
          </p>)}
      </div>
    </section>;
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1108" data-mp="1645kc5go35un">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950/95 backdrop-blur" data-id="element-1109" data-mp="1622lcvxje2ds">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8" data-id="element-1110" data-mp="112ye4au6aeq3">
          <Link to="/" className="shrink-0" data-id="element-1111" data-mp="366ez06ejrwjx">
            <span className="block font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-offwhite sm:text-3xl" data-id="element-1112" data-mp="365dcn5s2znmw">
              Grit<span className="text-grit-500" data-id="element-1113" data-mp="3azhiflpm1pme">Vue</span>
              <sup className="ml-0.5 align-super text-[9px] text-steel-400" data-id="element-1114" data-mp="3exf7mojdsbps">™</sup>
            </span>
            <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.3em] text-steel-400 sm:text-[9px]" data-id="element-1115" data-mp="2i9rda25yhy2f">
              See the job through
            </span>
          </Link>
          <Link to="/#offer" className="inline-flex shrink-0 items-center bg-grit-500 px-3.5 py-2.5 font-display text-base font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:px-4 sm:text-lg" data-id="element-1116" data-mp="2r82z7qvvo7oj">
            Get your X1
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-id="element-1117" data-mp="llf5il2rjcuy">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1118" data-mp="go55yclntkte">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1119" data-mp="7u9yuz0z9kb4" />
          Back to the X1
        </Link>

        {!doc ? <div className="mt-8" data-id="element-1120" data-mp="2t2u78u4t18o">
            <h1 className="grit-headline text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1121" data-mp="3u1gwym9npjkk">
              Page not found
            </h1>
            <p className="mt-4 text-lg text-steel-300" data-id="element-1122" data-mp="3p4gracgpyitz">Pick one of our policies below.</p>
          </div> : <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16" data-id="element-1123" data-mp="1nb0pcjf6lhjp">
            <article data-id="element-1124" data-mp="1n8xzbze13oez">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-1125" data-mp="1j9ypc0dfx1go">{doc.eyebrow}</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-1126" data-mp="1qcdidw1lig77">
                {doc.title}
              </h1>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-steel-500" data-id="element-1127" data-mp="1v9m2i26b0ub3">
                Last updated {doc.updated}
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-200" data-id="element-1128" data-mp="1lchedwsweorn">{doc.intro}</p>

              <div className="mt-10 grid gap-8" data-id="element-1129" data-mp="17lm5ikieg7o2">
                {doc.sections.filter(section => !section.fine).map(renderSection)}
              </div>

              <div className="mt-12 grid gap-px border-2 border-grit-500 bg-white/10 lg:grid-cols-[1.1fr_1fr]" data-id="element-1130" data-mp="xohpz3j27ag8">
                <img src={media.lensSheet} alt="GritVue X1 illuminated work glasses shown with its clear, amber and smoke lenses" className="block h-auto w-full bg-ink-950 object-contain" data-id="element-1131" data-mp="12mrnhks9an50" />
                <div className="flex flex-col justify-center bg-ink-950 p-6 lg:p-8" data-id="element-1132" data-mp="topi6t1hp8bx">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-1133" data-mp="a12zqceelx2z">
                    Complete system from $49.95
                  </p>
                  <p className="grit-headline mt-3 text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold uppercase text-offwhite" data-id="element-1134" data-mp="j2kcbkslrgmx">
                    Still want yours?
                    <br data-id="element-1135" data-mp="szfb84ssfj1q" />
                    <span className="text-grit-500" data-id="element-1136" data-mp="syx5mhk8jsh5">Pick your pack.</span>
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1137" data-mp="o1dtbn73lpru">
                    Every X1 ships as the complete system: 3 lenses, hard case, care kit, USB cable, anti-fog spray and
                    the Lifetime Replacement Warranty free on every multi-pack.
                  </p>
                  <Link to="/#offer" className="mt-6 inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-1138" data-mp="1bpesw6matvv4">
                    Back to the packs
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1139" data-mp="dcsukr22uklt" />
                  </Link>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-steel-400" data-id="element-1140" data-mp="i6n81cr3a3cg">
                    Buy 2, get 1 free: $99.90 · $33.30 per X1 · Free U.S. shipping
                  </p>
                </div>
              </div>

              {doc.sections.some(section => section.fine) && <div className="mt-10 grid gap-8" data-id="element-1141" data-mp="x34naf2eg24a">
                  {doc.sections.filter(section => section.fine).map(renderSection)}
                </div>}

              <div className="mt-8 flex flex-col gap-4 border border-white/12 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1142" data-mp="2yv1526f5u87i">
                <p data-id="element-1143" data-mp="32x4hoydkfiud">
                  <span className="block font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-1144" data-mp="2vcbt7hv53f54">
                    Still have a question?
                  </span>
                  <a href="mailto:support@gritvuegear.com" className="mt-2 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1145" data-mp="2llz194kzwjoy">
                    <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1146" data-mp="3e8aaf3mo3sxd" />
                    support@gritvuegear.com
                  </a>
                </p>
                <Link to="/#offer" className="inline-flex items-center justify-center bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px]" data-id="element-1147" data-mp="358eukli639r0">
                  Shop the X1
                </Link>
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1148" data-mp="3szkxdhpigz4d">
              <nav aria-label="Policies" className="border border-white/12 bg-ink-900" data-id="element-1149" data-mp="3t0vyf9rknsrs">
                <p className="border-b border-white/10 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1150" data-mp="3f1cqoi2oajg0">
                  Policies
                </p>
                <ul data-id="element-1151" data-mp="bk4bk5zbxxoz">
                  {legalDocs.map(item => <li key={item.slug} className="border-b border-white/5 last:border-b-0" data-id="element-1152" data-mp="ghwgc0l2peal">
                      <Link to={`/legal/${item.slug}`} className={`block px-5 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-150 ease-grit ${item.slug === doc.slug ? 'bg-grit-500/10 text-grit-500' : 'text-steel-300 hover:text-offwhite'}`} data-id="element-1153" data-mp="1t9s19dgxc78">
                        {item.title}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <ul className="mt-6 grid gap-3 border border-white/12 bg-ink-900 p-5" data-id="element-1154" data-mp="2srw8z1xvii3z">
                {promises.map(promise => {
              const Icon = promise.icon;
              return <li key={promise.label} className="flex items-start gap-3 text-[14px] font-medium text-steel-200" data-id="element-1155" data-mp="20zosq2ma57q4">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1156" data-mp="1w25c4w240iv0" />
                      {promise.label}
                    </li>;
            })}
              </ul>

              <div className="mt-6" data-id="element-1157" data-mp="2508qn8eogfdb">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-steel-500" data-id="element-1158" data-mp="3lg1sxmh3s32r">Secure checkout</p>
                <div className="mt-2" data-id="element-1159" data-mp="3ghgt3o5rjleu">
                  <PaymentLogos data-id="element-1160" data-mp="3ggfhniku4wwl" />
                </div>
              </div>
            </aside>
          </div>}
      </main>

      <SiteFooter data-id="element-1161" data-mp="17c221g6mjx1u" />
    </div>;
}